import type { EndlessEntry } from '../sim/defs';

/* ═══════════════════════════════════════════════════════════════════════════
   LA STRUCTURE À DEUX ÉTAGES : Mondes → Niveaux.

   Jusqu'ici « monde » et « niveau » étaient le même objet — une liste plate
   d'identifiants, chacun un `LevelDef` d'une seule coulée de quatre minutes.
   Cette structure loge désormais des NIVEAUX à l'intérieur d'un monde : chaque
   monde est une suite ordonnée de niveaux courts, et SEUL LE DERNIER porte un
   boss. Les autres se terminent à l'horloge.

   ── Pourquoi le monde porte le `hpScale`, pas le niveau ──
   Le coefficient de PV est une propriété du monde traversé, partagée par tous
   ses niveaux. Il vit donc ICI et non sur chaque `LevelDef` : la couche
   application le fournit à `startLevel`, et `levelSystem` le lit dans `run`.

   ⚠️ Cette liste n'est PAS ramassée par un glob : l'ordre compte, et un glob
   trie par chemin. Il est écrit, donc il est relu.

   Chaque monde contient désormais TROIS niveaux (`monde-N-1/2/3`). Les deux
   premiers se terminent à l'horloge ; le troisième porte le boss. L'identifiant
   du MONDE (`monde-N`) n'est plus celui d'un niveau — il sert de clé aux jalons
   (`monde-N-franchi`) et à l'affichage, jamais à `startLevel`, qui reçoit un
   identifiant de NIVEAU via `levelAt`.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface WorldDef {
  /** Identifiant stable — sert de clé aux jalons (`monde-1-franchi`) et à la
   *  sauvegarde. Ne pas renommer sans migrer les jalons. */
  readonly id: string;
  /** Nom affiché du monde. */
  readonly name: string;
  /** Coefficient de PV partagé par tous les niveaux du monde (PLAN.md §6.4). */
  readonly hpScale: number;
  /** Niveaux du monde, dans l'ordre. Le DERNIER porte le boss. */
  readonly levels: readonly string[];

  /* ── TABLE D'IDENTITÉ (optionnelle) ──────────────────────────────────────────
     Ces champs regroupent en UN SEUL endroit déclaratif ce qui distingue un monde
     d'un autre — sa palette, son bestiaire, son décor, son boss. Ils sont le point
     d'entrée du GÉNÉRATEUR (`content/generate.ts` : un `WorldPlan` s'en déduit) :
     un nouveau monde se décrit par son identité, et son contenu (niveaux/vagues)
     est produit plutôt qu'écrit à la main.

     Ils sont OPTIONNELS et rétro-compatibles : les mondes historiques (dont les
     niveaux sont écrits à la main) ne les renseignent pas, et rien ne change pour
     eux. Ils deviennent nécessaires dès qu'on GÉNÈRE un monde. */
  /** Identifiant de l'étalonnage couleur (LUT) du monde. Absent → l'étalonnage se
   *  résout par le rang du monde (comportement historique). Le renseigner permet
   *  de découpler identité visuelle et position dans la campagne. */
  readonly gradeId?: string;
  /** Roster tiéré du bestiaire — l'IDENTITÉ de menace du monde. Mêmes règles que
   *  le mode sans fin (`EndlessEntry`). Consommé par le générateur de vagues. */
  readonly roster?: readonly EndlessEntry[];
  /** Types d'obstacles disponibles dans ce monde (astéroïdes, complexes…). */
  readonly obstacleSet?: readonly string[];
  /** Identifiant de la vague de boss du dernier niveau (préfixe `boss-`). */
  readonly bossWave?: string;
}

export const WORLDS: readonly WorldDef[] = [
  {
    id: 'monde-1',
    name: 'La Descente',
    hpScale: 1,
    gradeId: 'fer',
    levels: ['monde-1-1', 'monde-1-2', 'monde-1-3'],
  },
  {
    id: 'monde-2',
    name: 'Le Champ',
    hpScale: 1.6,
    gradeId: 'cendre',
    levels: ['monde-2-1', 'monde-2-2', 'monde-2-3'],
  },
  {
    id: 'monde-3',
    name: 'Le Cœur du Secteur',
    hpScale: 2.4,
    gradeId: 'forge',
    levels: ['monde-3-1', 'monde-3-2', 'monde-3-3'],
  },
];

/** Coefficient de PV suggéré pour le monde de rang `index` (0-based), sur une
 *  COURBE géométrique croissante — l'outil pour dimensionner un NOUVEAU monde
 *  sans casser la monotonie (`campaign.test.ts` exige des `hpScale` strictement
 *  croissants). Les trois mondes historiques gardent leurs valeurs réglées à la
 *  main (1 / 1,6 / 2,4) ; la courbe les approche et prolonge au-delà. Base 1,55 :
 *  1,55⁰≈1, 1,55¹≈1,55, 1,55²≈2,40 — puis 3,72, 5,77… La compensation par la
 *  courbe de puissance du build reste la contrepartie (PLAN.md §6.4). */
export function hpScaleForWorld(index: number): number {
  let raw = 1;
  for (let i = 0; i < index; i++) raw *= 1.55;
  // Arrondi à 0,05 près : des coefficients lisibles, toujours strictement
  // croissants (la base > 1 le garantit avant arrondi, et le pas d'arrondi est
  // bien plus fin que l'écart entre deux puissances successives).
  return Math.round(raw * 20) / 20;
}

/** Un repère de position dans la campagne : quel monde, quel niveau. */
export interface CampaignSpot {
  readonly worldIndex: number;
  readonly levelIndex: number;
}

export const CAMPAIGN_START: CampaignSpot = { worldIndex: 0, levelIndex: 0 };

/** Identifiant du niveau à une position donnée, ou `null` si hors bornes. */
export function levelAt(spot: CampaignSpot): string | null {
  const world = WORLDS[spot.worldIndex];
  if (!world) return null;
  return world.levels[spot.levelIndex] ?? null;
}

/** Le niveau à cette position est-il le DERNIER de son monde (donc le boss) ? */
export function isLastLevelOfWorld(spot: CampaignSpot): boolean {
  const world = WORLDS[spot.worldIndex];
  if (!world) return false;
  return spot.levelIndex >= world.levels.length - 1;
}

/**
 * Position suivante, ou `null` si la campagne est finie.
 *
 * ⚠️ Deux franchissements distincts : passer au niveau suivant du MÊME monde,
 * ou — si c'était le dernier niveau — passer au premier niveau du monde
 * suivant. La couche application distingue les deux (carte à chaque niveau,
 * jalon « monde franchi » au seul passage de monde).
 */
export function nextSpot(spot: CampaignSpot): CampaignSpot | null {
  const world = WORLDS[spot.worldIndex];
  if (!world) return null;
  if (spot.levelIndex + 1 < world.levels.length) {
    return { worldIndex: spot.worldIndex, levelIndex: spot.levelIndex + 1 };
  }
  if (spot.worldIndex + 1 < WORLDS.length) {
    return { worldIndex: spot.worldIndex + 1, levelIndex: 0 };
  }
  return null;
}

/** Rang du monde (1-based) à une position, pour l'affichage. */
export function worldNumberOf(spot: CampaignSpot): number {
  return spot.worldIndex + 1;
}

/** Rang du niveau (1-based) dans son monde, pour l'affichage. */
export function levelNumberOf(spot: CampaignSpot): number {
  return spot.levelIndex + 1;
}

/** Nombre de niveaux d'un monde, pour l'affichage « niveau Y/Z ». */
export function levelCountOf(worldIndex: number): number {
  return WORLDS[worldIndex]?.levels.length ?? 0;
}

/** Identifiant du DERNIER niveau d'un monde — celui qui porte le boss. `null`
 *  hors bornes. Sert à l'entraînement (boss seul) et aux tests d'équilibrage,
 *  qui raisonnent par monde mais doivent atteindre le niveau du boss. */
export function bossLevelId(worldIndex: number): string | null {
  const world = WORLDS[worldIndex];
  if (!world) return null;
  return world.levels[world.levels.length - 1] ?? null;
}

export const WORLD_COUNT = WORLDS.length;

/* ── Compatibilité ─────────────────────────────────────────────────────────
   Tant que chaque monde ne contient qu'un niveau, l'identifiant d'un monde
   égale celui de son unique niveau. Ces helpers, hérités de la structure plate,
   opèrent sur les identifiants de MONDES et restent justes dans ce cas. Ils
   disparaîtront quand tout sera passé au repère `CampaignSpot`. */

/** Identifiants des mondes, dans l'ordre. */
export const CAMPAIGN: readonly string[] = WORLDS.map((w) => w.id);

/** Monde suivant par identifiant, ou `null` si c'était le dernier. */
export function nextWorld(id: string | null): string | null {
  if (id === null) return null;
  const i = WORLDS.findIndex((w) => w.id === id);
  if (i < 0 || i + 1 >= WORLDS.length) return null;
  return WORLDS[i + 1]!.id;
}

/** Rang (1-based) d'un monde par identifiant. Zéro si inconnu. */
export function worldNumber(id: string | null): number {
  if (id === null) return 0;
  return WORLDS.findIndex((w) => w.id === id) + 1;
}

/** Rang (1-based) du monde qui CONTIENT un identifiant de NIVEAU. Zéro si le
 *  niveau n'appartient à aucun monde (mode sans fin, exercice hors campagne).
 *
 *  ⚠️ Distinct de `worldNumber`, qui prend un id de MONDE : depuis le découpage à
 *  deux étages, `run.levelId` vaut « monde-N-K » (un niveau), pas « monde-N » (un
 *  monde). La couche vue s'en sert pour l'étalonnage couleur par monde — lui
 *  passer un id de niveau via `worldNumber` renverrait toujours 0, et toute la
 *  campagne partagerait l'étalonnage du premier monde. */
export function worldNumberOfLevel(levelId: string | null): number {
  if (levelId === null) return 0;
  const i = WORLDS.findIndex((w) => w.levels.includes(levelId));
  return i + 1;
}

/** Coefficient de PV d'un monde par identifiant. 1 si inconnu. */
export function hpScaleOf(id: string | null): number {
  if (id === null) return 1;
  return WORLDS.find((w) => w.id === id)?.hpScale ?? 1;
}
